import { create } from 'zustand';
import { User, PdfDocument, HistologyImage, PracticeSession, ImageAnnotation, ImageFilters } from '../types';
import { generateId } from '../lib/utils';
import { supabase } from '../lib/supabase';

interface AppState {
  user: User | null;
  isAuthInitialized: boolean;
  pdfs: PdfDocument[];
  images: HistologyImage[];
  sessions: PracticeSession[];
  isLoading: boolean;
  
  // Auth Actions
  setUser: (user: User | null) => void;
  setAuthInitialized: (val: boolean) => void;
  logout: () => Promise<void>;
  
  // Data Actions
  fetchUserData: () => Promise<void>;
  getPdfImages: (pdfId: string) => HistologyImage[];
  createPdfRecord: (file: File) => Promise<PdfDocument>;
  addMultipleProcessedImages: (items: { pdfId: string, imageBase64: string, originalName: string, label: string }[]) => Promise<void>;
  updateImageLabel: (id: string, label: string) => Promise<void>;
  updateImageMetadata: (id: string, updates: Partial<HistologyImage>) => Promise<void>;
  bulkUpdateLabels: (ids: string[], label: string) => Promise<void>;
  deleteImages: (ids: string[]) => Promise<void>;
  saveSession: (session: PracticeSession) => Promise<void>;
  
  getStats: () => { totalPdfs: number; totalImages: number; totalSessions: number; avgScore: number };
}

export const useAppStore = create<AppState>((set, get) => ({
  user: null, 
  isAuthInitialized: false,
  pdfs: [],
  images: [],
  sessions: [],
  isLoading: false,

  setUser: (user) => {
    set({ user });
    if (user) {
      get().fetchUserData();
    }
  },

  setAuthInitialized: (val: boolean) => {
    set({ isAuthInitialized: val });
  },

  logout: async () => {
    await supabase.auth.signOut();
    set({ user: null, pdfs: [], images: [], sessions: [] });
  },

  fetchUserData: async () => {
    const user = get().user;
    if (!user) return;

    set({ isLoading: true });
    try {
      const { data: pdfs } = await supabase
        .from('pdfs')
        .select('*')
        .eq('user_id', user.id)
        .order('uploaded_at', { ascending: false });

      const { data: images } = await supabase
        .from('histology_images')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      const { data: sessions } = await supabase
        .from('practice_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('started_at', { ascending: false });

      set({ 
        pdfs: pdfs || [], 
        images: images || [], 
        sessions: sessions || [] 
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  getPdfImages: (pdfId: string) => {
    return get().images.filter(img => img.pdf_id === pdfId);
  },

  createPdfRecord: async (file: File) => {
    const userId = get().user?.id;
    
    const newPdf: any = {
      id: generateId(),
      user_id: userId || 'guest',
      filename: file.name,
      storage_path: `documents/${generateId()}_${file.name}`,
      status: 'processed',
      uploaded_at: new Date().toISOString(),
      size_mb: parseFloat((file.size / (1024 * 1024)).toFixed(2)),
    };

    if (userId) {
      const { data, error } = await supabase
        .from('pdfs')
        .insert(newPdf)
        .select()
        .single();

      if (error) throw error;
      set(state => ({ pdfs: [data, ...state.pdfs] }));
      return data;
    } else {
      set(state => ({ pdfs: [newPdf, ...state.pdfs] }));
      return newPdf;
    }
  },

  addMultipleProcessedImages: async (items) => {
    const userId = get().user?.id;

    const newImages = items.map(item => ({
        id: generateId(),
        user_id: userId || 'guest',
        pdf_id: item.pdfId,
        image_url: item.imageBase64, 
        original_filename: item.originalName,
        identified_organ_tissue: item.label,
        annotations: [],
        filters: { brightness: 100, contrast: 100, grayscale: false, inverted: false },
        created_at: new Date().toISOString()
    }));

    if (userId) {
      const { data, error } = await supabase
        .from('histology_images')
        .insert(newImages)
        .select();

      if (error) throw error;
      set(state => ({ images: [...(data || []), ...state.images] }));
    } else {
      set(state => ({ images: [...newImages, ...state.images] }));
    }
  },

  updateImageLabel: async (id, label) => {
    get().updateImageMetadata(id, { identified_organ_tissue: label });
  },

  updateImageMetadata: async (id, updates) => {
    const userId = get().user?.id;
    
    set(state => ({
      images: state.images.map(img => img.id === id ? { ...img, ...updates } : img)
    }));

    if (userId) {
      const { error } = await supabase
        .from('histology_images')
        .update(updates)
        .eq('id', id);
      
      if (error) console.error("Failed to sync metadata update:", error);
    }
  },

  bulkUpdateLabels: async (ids, label) => {
    const userId = get().user?.id;
    
    set(state => ({
      images: state.images.map(img => ids.includes(img.id) ? { ...img, identified_organ_tissue: label } : img)
    }));

    if (userId) {
      const { error } = await supabase
        .from('histology_images')
        .update({ identified_organ_tissue: label })
        .in('id', ids);
      
      if (error) {
        console.error("Failed to sync bulk update:", error);
        get().fetchUserData();
      }
    }
  },

  deleteImages: async (ids: string[]) => {
    const userId = get().user?.id;
    
    set(state => ({
      images: state.images.filter(img => !ids.includes(img.id))
    }));

    if (userId) {
      const { error } = await supabase
        .from('histology_images')
        .delete()
        .in('id', ids);
      
      if (error) {
        console.error("Failed to sync bulk deletion:", error);
        get().fetchUserData();
      }
    }
  },

  saveSession: async (session: PracticeSession) => {
    const userId = get().user?.id;

    if (userId) {
      const { data, error } = await supabase
        .from('practice_sessions')
        .insert({ ...session, user_id: userId })
        .select()
        .single();

      if (error) throw error;
      set(state => ({ sessions: [data, ...state.sessions] }));
    } else {
      set(state => ({ sessions: [session, ...state.sessions] }));
    }
  },

  getStats: () => {
    const { pdfs, images, sessions } = get();
    const totalPdfs = pdfs.length;
    const totalImages = images.length;
    const totalSessions = sessions.length;
    const avgScore = sessions.length > 0 
      ? sessions.reduce((acc, curr) => acc + curr.score_percentage, 0) / sessions.length 
      : 0;

    return { totalPdfs, totalImages, totalSessions, avgScore };
  }
}));